import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { MessageSquareText, Palette } from "lucide-react";
import { SectionCard, TextRow, ListEditor, RowsEditor } from "@/components/Brand/editors";

export default function BrandIdentityTab({ brand, onSaved }) {
  const [voice, setVoice] = useState(brand.voice || {});
  const [visual, setVisual] = useState(brand.visual || {});
  const [busy, setBusy] = useState(false);
  const setVo = (k, v) => setVoice((s) => ({ ...s, [k]: v }));
  const setVi = (k, v) => setVisual((s) => ({ ...s, [k]: v }));

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="brand-identity-tab">
      <SectionCard title="Verbal identity — voice & tone" icon={MessageSquareText} onSave={() => save({ voice })} busy={busy} testid="brand-voice-section">
        <ListEditor label="Voice attributes" items={voice.attributes || []} onChange={(v) => setVo("attributes", v)} placeholder="e.g. Confident, witty…" testid="voice-attributes" />
        <RowsEditor label="Tone by context"
          columns={[{ key: "context", label: "Context (social / ads / email / support)" }, { key: "tone", label: "Tone", type: "textarea" }]}
          rows={voice.tone_matrix || []} onChange={(v) => setVo("tone_matrix", v)} addLabel="Add context" testid="tone-matrix" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ListEditor label="Do's" items={voice.dos || []} onChange={(v) => setVo("dos", v)} testid="voice-dos" />
          <ListEditor label="Don'ts" items={voice.donts || []} onChange={(v) => setVo("donts", v)} testid="voice-donts" />
        </div>
        <ListEditor label="Banned words / topics" items={voice.banned || []} onChange={(v) => setVo("banned", v)} testid="voice-banned" />
        <RowsEditor label="Boilerplates"
          columns={[{ key: "label", label: "Label" }, { key: "text", label: "Text", type: "textarea" }]}
          rows={voice.boilerplates || []} onChange={(v) => setVo("boilerplates", v)} addLabel="Add boilerplate" testid="boilerplates" />
        <RowsEditor label="Key messages by audience"
          columns={[{ key: "audience", label: "Audience" }, { key: "message", label: "Message", type: "textarea" }]}
          rows={voice.key_messages || []} onChange={(v) => setVo("key_messages", v)} addLabel="Add message" testid="key-messages" />
      </SectionCard>

      <SectionCard title="Visual identity" icon={Palette} onSave={() => save({ visual })} busy={busy} testid="brand-visual-section">
        <TextRow label="Logo usage rules" value={visual.logo_rules} onChange={(v) => setVi("logo_rules", v)} multiline testid="visual-logo-rules" />
        <RowsEditor label="Color palette"
          columns={[{ key: "name", label: "Name" }, { key: "hex", label: "Color", type: "color" }]}
          rows={visual.colors || []} onChange={(v) => setVi("colors", v)} addLabel="Add color" testid="colors" />
        {(visual.colors || []).length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {(visual.colors || []).map((c, i) => (
              <div key={i} className="w-12 h-12 border border-border flex items-end justify-center pb-0.5" style={{ backgroundColor: c.hex }} title={`${c.name} ${c.hex}`}>
                <span className="text-[8px] font-mono bg-white/80 px-0.5">{c.hex}</span>
              </div>
            ))}
          </div>
        )}
        <RowsEditor label="Typography"
          columns={[{ key: "use", label: "Use (heading / body / accent)" }, { key: "font", label: "Font family" }]}
          rows={visual.typography || []} onChange={(v) => setVi("typography", v)} addLabel="Add font" testid="typography" />
        <TextRow label="Imagery style" value={visual.imagery_style} onChange={(v) => setVi("imagery_style", v)} multiline testid="visual-imagery" />
        <RowsEditor label="Asset links (brand kit, Figma, DAM…)"
          columns={[{ key: "label", label: "Label" }, { key: "url", label: "URL" }]}
          rows={visual.asset_links || []} onChange={(v) => setVi("asset_links", v)} addLabel="Add link" testid="asset-links" />
      </SectionCard>
    </div>
  );
}
